import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppPartnerService } from '@galaxy/marketplace-apps';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { Edition, getEditionFromId } from '../common/editions';
import { ApiRoutesV1 } from '../common/routes';
import { CustomerVoiceRouterService } from './cv-router.service';

export class AppConfig {
  constructor(
    public readonly vendorCenterAppId: string,
    public readonly partnerId: string,
    public readonly marketId: string,
    public readonly country: string,
    public readonly businessName: string,
    public readonly sessionId: string,
    public readonly subjectId: string,
    public readonly unifiedUserId: string,
    public readonly userEmail: string,
    public readonly sourceIdNameMap: Map<number, string>,
    public readonly isPaidVersion: boolean,
    public readonly navbarDataUrl: string,
    public readonly partnerName: string,
    public readonly partnerLogo: string,
    public readonly isSMB: string,
    public readonly edition: Edition,
    public readonly editionIds: { pro: string; express: string },
    public readonly isEmailFooterLanaguageEnabled: boolean,
    public readonly hideUpgradeCTA?: boolean,
    public readonly twilioSubaccountId?: string,
  ) {}
}

@Injectable({ providedIn: 'root' })
export class AppConfigService {
  private readonly appConfigSubject: BehaviorSubject<AppConfig> = new BehaviorSubject<AppConfig>(null);

  constructor(
    private customerVoiceRouter: CustomerVoiceRouterService,
    private http: HttpClient,
    private appPartnerService: AppPartnerService,
  ) {
    this.customerVoiceRouter
      .getRouteUrl(ApiRoutesV1.appConfig)
      .pipe(
        switchMap((appConfigUrl: string) => {
          return this.http.get(appConfigUrl);
        }),
        switchMap((response: any) => {
          const config = response.data;
          return this.appPartnerService.getAppSettings(config.appId, config.partnerId, config.marketId).pipe(
            map((appSetting) => {
              config.hideUpgradeCTA = false;
              if (!!appSetting && !!appSetting.editionChange && appSetting.editionChange.hideUpgradeCta) {
                config.hideUpgradeCTA = true;
              }
              return config;
            }),
            catchError(() => of(config)),
          );
        }),
      )
      .subscribe((config) => {
        const isPaidVersion = !config.legacyAccount;
        const appConfig: AppConfig = new AppConfig(
          config.appId,
          config.partnerId,
          config.marketId,
          config.country,
          config.businessName,
          config.sessionId,
          config.subjectId,
          config.unifiedUserId,
          config.userEmail,
          config.sourceIdNameMapping,
          isPaidVersion,
          config.navbarDataUrl,
          config.partnerName,
          config.partnerLogo,
          config.is_smb_user,
          getEditionFromId(config.edition, isPaidVersion),
          config.editionIds,
          config.cvEmailFooterLanguageEnabled,
          config.hideUpgradeCTA,
          config.twilioSubaccountId,
        );
        this.appConfigSubject.next(appConfig);
      });
  }

  public get appConfig(): Observable<AppConfig> {
    return this.appConfigSubject.asObservable().pipe(filter((appConfig) => appConfig !== null));
  }
}
